import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, ImagePlus, Save, Shield, Type, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API, BACKEND_URL } from '@/lib/config';
import { useBranding } from '@/context/BrandingContext';

export default function AdminBrandingPage() {
  const navigate = useNavigate();
  const { refreshBranding } = useBranding();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    system_name: '',
    logo_url: ''
  });

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      const response = await axios.get(`${API}/branding`);
      setFormData({
        system_name: response.data?.system_name || '',
        logo_url: response.data?.logo_url || ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar branding');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Envie um arquivo de imagem valido');
      return;
    }

    setUploading(true);
    try {
      const payload = new FormData();
      payload.append('file', file);
      const response = await axios.post(`${API}/upload-image`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData((prev) => ({ ...prev, logo_url: response.data.url }));
      toast.success('Logo enviada com sucesso');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/branding`, {
        system_name: formData.system_name,
        logo_url: formData.logo_url || null
      });
      await refreshBranding();
      toast.success('Branding atualizado com sucesso');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar branding');
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = formData.logo_url
    ? (formData.logo_url.startsWith('http') ? formData.logo_url : `${BACKEND_URL}${formData.logo_url}`)
    : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/40 bg-white/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center gap-4">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-primary-foreground" />
            </div>
            <h1 className="text-xl font-manrope font-bold tracking-tight">Branding do Sistema</h1>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-12 space-y-8">
        <div>
          <h2 className="text-3xl font-manrope font-bold tracking-tight mb-2">Nome e logo</h2>
          <p className="text-muted-foreground font-inter">
            Altere como o sistema aparece para os usuarios nas telas principais.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_0.9fr] gap-6">
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-border/40 p-8 shadow-sm space-y-6">
            <div className="space-y-2">
              <Label htmlFor="system_name" className="flex items-center gap-2">
                <Type className="w-4 h-4" />
                Nome do sistema
              </Label>
              <Input
                id="system_name"
                value={formData.system_name}
                onChange={(e) => setFormData((prev) => ({ ...prev, system_name: e.target.value }))}
                placeholder="Ex: Tecnoservice Brasil"
                required
              />
            </div>

            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <ImagePlus className="w-4 h-4" />
                Logo
              </Label>

              {previewUrl ? (
                <div className="relative border border-border/40 rounded-2xl p-4 bg-gray-50">
                  <img src={previewUrl} alt="Logo atual" className="h-32 object-contain mx-auto" />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-3 right-3 rounded-full"
                    onClick={() => setFormData((prev) => ({ ...prev, logo_url: '' }))}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-border/50 rounded-2xl cursor-pointer hover:bg-gray-50 transition-colors">
                  {uploading ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  ) : (
                    <>
                      <ImagePlus className="w-10 h-10 text-muted-foreground mb-3" />
                      <p className="text-sm text-muted-foreground">Clique para enviar o logo</p>
                    </>
                  )}
                  <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={uploading} />
                </label>
              )}

              <div className="space-y-2">
                <Label htmlFor="logo_url">Ou informe uma URL de logo</Label>
                <Input
                  id="logo_url"
                  value={formData.logo_url}
                  onChange={(e) => setFormData((prev) => ({ ...prev, logo_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button type="submit" disabled={saving} className="rounded-full">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Salvando...' : 'Salvar Branding'}
              </Button>
            </div>
          </form>

          <div className="bg-white rounded-2xl border border-border/40 p-8 shadow-sm">
            <p className="text-sm text-muted-foreground mb-4">Pre-visualizacao</p>
            <div className="border border-border/40 rounded-2xl p-6 bg-gradient-to-br from-white to-gray-50">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center overflow-hidden">
                  {previewUrl ? (
                    <img src={previewUrl} alt="Preview da logo" className="w-full h-full object-contain bg-white" />
                  ) : (
                    <Calendar className="w-6 h-6 text-primary-foreground" />
                  )}
                </div>
                <h3 className="text-2xl font-manrope font-bold tracking-tight">
                  {formData.system_name || 'Nome do sistema'}
                </h3>
              </div>
              <p className="text-muted-foreground">
                O nome e o logo aparecerao na tela de login e nos cabecalhos administrativos.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
